import { useMsal } from '@azure/msal-react'
import { createStyles } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Routes, useNavigate } from 'react-router-dom'
import { axiosManager, baseURL } from './api'
import { AppHeader } from './components/app-header'
import { AppProgressIndicator } from './components/app-progress-indicator'
import { CookieBanner } from './components/cookie-banner'
import { EulaAgreementDialog } from './components/eula-agreement-dialog'
import { NewFooter } from './components/new-footer'
import { AppRouter } from './components/router'
import { ServiceUnavailable } from './components/service-unavailable'
import { StagingBanner } from './components/staging-banner/staging-banner'
import { msalRequest } from './env-config'
import { AppDialog } from './hooks/use-app-dialog/use-app-dialog.component'
import { useMsalAccessTokenProvider } from './hooks/use-fetch-with-msal/use-msal-access-token'
import { useTitle } from './hooks/use-title/use-title'
import { MainPage } from './pages/main-page'
import { setInitial, setIsShowAction } from './store/app-dialog/reducers'
import { BundleNotificationsDrawerProvider } from './contexts/bundle-notifications-drawer.context'
import {
  selectActions,
  selectCloseHandler,
  selectIsShow,
  selectMessage,
  selectShowCloseButton,
  selectTitle,
  selectVariant,
} from './store/app-dialog/selectors'
import {
  getUser,
  setHeaderAvatar,
  setServiceUnavailableMessage,
} from './store/auth/reducer'
import {
  selectServiceUnavailableMessage,
  selectUser,
} from './store/auth/selectors'
import { useNotifications } from './store/notifications/hooks'
import { updateNotifications } from './store/notifications/reducer'
import { selectOverlayPreloaderIsShow } from './store/overlay-preloader/selectors'
import theme from './theme'
import { colorAppCanvasBackground } from './shared/style-settings'

/* Time Interval */
const TIME_INTERVAL = 180000
const NOTIFICATION_UPDATE_TIME_INTERVAL = 300000

/* Styles */
const useStyles = makeStyles(() =>
  createStyles({
    mainContainer: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',

      background: colorAppCanvasBackground,
      ['@media print']: {
        display: 'none',
      },
    },
    content: {
      flexGrow: 1,
      paddingBottom: theme.spacing(2),
    },
    miniOverlay: {
      height: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  })
)

export const AppWrapper: React.FC = () => {
  const { authMsalResult } = useMsalAccessTokenProvider()
  const { instance } = useMsal()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const classes = useStyles()
  const appDialogMessage = useSelector(selectMessage)
  const appDialogTitle = useSelector(selectTitle)
  const appDialogIsShow = useSelector(selectIsShow)
  const appDialogVariant = useSelector(selectVariant)
  const appDialogShowCloseButton = useSelector(selectShowCloseButton)
  const appDialogCloseHandler = useSelector(selectCloseHandler)
  const appDialogActions = useSelector(selectActions)
  const overlayPreloaderIsShow = useSelector(selectOverlayPreloaderIsShow)
  const user = useSelector(selectUser)
  const serviceUnavailableMessage = useSelector(selectServiceUnavailableMessage)
  const { setTitle } = useTitle()

  const { fetchNotifications } = useNotifications()

  /* Reset Handler */
  const resetHandler = () => {
    dispatch(setInitial())
  }

  /* Set Is Show */
  const appDialogSetIsShow = (value: boolean): void => {
    dispatch(setIsShowAction(value))
  }

  /* Get User */
  const getUserCallback = () => {
    dispatch(getUser())
  }

  /* Navigate to the state */
  useEffect(() => {
    if (authMsalResult.result?.state) {
      navigate(authMsalResult.result?.state)
    }
  }, [authMsalResult.result?.state])

  /* Set Title */
  useEffect(() => {
    setTitle('Prosphora')
  }, [])

  /* Set Access To Local Storage */
  useEffect(() => {
    const token = authMsalResult.result?.accessToken || null
    axiosManager.setToken(token)
    const { error } = authMsalResult
    if (error || token) {
      getUserCallback()
    }
  }, [
    authMsalResult.error,
    authMsalResult.result,
    authMsalResult.result?.accessToken,
  ])

  // Set Access Token to Local Storage
  useEffect(() => {
    const intervalId = setInterval(() => {
      instance.acquireTokenSilent(msalRequest).then((data) => {
        axiosManager.setToken(data.accessToken)
      })
    }, TIME_INTERVAL)
    return () => clearInterval(intervalId)
  })

  /* Set Avatar */
  useEffect(() => {
    user &&
      user.userId &&
      dispatch(setHeaderAvatar(`${baseURL}avatar/${user.userId}/small.jpg`))
  }, [user])

  // Get notifications
  useEffect(() => {
    //setLoadingInStore(LoadingState.LOADING)
    if (user?.userId) {
      fetchNotifications({
        userId: user.userId,
      })
    }
  }, [user.userId])

  // Periodically update notifications if user is authenticated
  useEffect(() => {
    if (!user?.userId) {
      return
    }

    const intervalId = setInterval(() => {
      dispatch(updateNotifications({ userId: user.userId }))
    }, NOTIFICATION_UPDATE_TIME_INTERVAL)

    return () => clearInterval(intervalId)
  }, [user?.userId, dispatch])

  return (
    <BundleNotificationsDrawerProvider>
      <AppDialog
        title={appDialogTitle}
        showCloseButton={appDialogShowCloseButton}
        message={appDialogMessage}
        variant={appDialogVariant}
        isShow={appDialogIsShow}
        closeHandler={appDialogCloseHandler}
        setIsShow={appDialogSetIsShow}
        actions={appDialogActions}
        resetHandler={resetHandler}
      />
      {overlayPreloaderIsShow && <AppProgressIndicator />}
      <div>
        <StagingBanner
          isTest={window.globalConfig.REACT_APP_DISPLAY_STAGING_STATUS}
        />
        {serviceUnavailableMessage && (
          <ServiceUnavailable
            message={serviceUnavailableMessage}
            onClose={() =>
              dispatch(
                setServiceUnavailableMessage({
                  serviceUnavailableMessage: null,
                })
              )
            }
          />
        )}
        <Routes>
          <Route path="/" element={<MainPage></MainPage>}></Route>
          <Route
            path="/*"
            element={
              <>
                <div className={`App ${classes.mainContainer}`}>
                  <AppHeader />
                  <div className={classes.content}>
                    <AppRouter />
                  </div>
                  <NewFooter />
                </div>
              </>
            }
          ></Route>
        </Routes>
        <EulaAgreementDialog />
        <CookieBanner />
      </div>
    </BundleNotificationsDrawerProvider>
  )
}
